import { ArticleCard, Box, Button, EventCard, Flex } from '@cowsquare/design';
import { Link } from 'gatsby';
import gql from 'graphql-tag';
import * as R from 'ramda';
import React from 'react';
import { Query } from 'react-apollo';
import { Helmet } from 'react-helmet';
import styled, { css } from 'styled-components';
import { useAdsQuery } from '../../../../utils/hooks';
import { isExpired } from '../../../../utils/isExpired';
import { Advertisements } from '../../../Advertisements';
import { getItemProps as getArticleCardProps } from '../../../ArticleCardList';
import { getItemProps as getEventCardProps } from '../../../EventCardList';
import { Section } from '../../../Section';
import { Title } from '../../../Title';

const ListFlex = styled(Flex)`
  & > :last-child {
    margin: 0;
  }
`;

const EventCardBox = styled(Box).attrs({
  mb: [5, 0],
  px: [0, 4],
  width: [1, 1 / 3],
})`
  overflow: hidden;
  ${props =>
    props.expired &&
    css`
      opacity: 0.5;
    `}
`;

const GET_CME_HOME_DATA = gql`
  query getCMEHomeData {
    events: hkdutest_cme_events(limit: 4, order_by: { date: desc }) {
      id
      name
      start_time
      end_time
      price
      date
      content
      location
      score
      organizer {
        id
        name
      }
      tags {
        tag {
          name
        }
      }
    }
    videos: hkdutest_cme_materials(
      limit: 6
      order_by: { created_at: desc }
      where: { publish_at: { _lt: "now" } }
    ) {
      id
      author {
        real_avatar
        name
        email
      }
      name
      content
      cover_file {
        link
      }
      tags {
        tag {
          name
        }
      }
      created_at
    }
  }
`;

const takeMap = R.useWith(R.pipe, [R.take, R.map]);

const Home = ({ uri }) => {
  const { ads } = useAdsQuery(uri);
  return (
    <Query query={GET_CME_HOME_DATA}>
      {({ data }) => {
        const events = R.propOr([], 'events', data);
        const videos = R.propOr([], 'videos', data);
        return (
          <>
            <Helmet>
              <title>Home - HKDU CME</title>
            </Helmet>
            <Advertisements ads={ads} itemProps={{ height: ['150px', '300px'] }} />
            <Section>
              <Flex flexDirection="column">
                <Box px={[5, 0]} py={8}>
                  <Flex flexDirection="column">
                    <Box mb={8}>
                      <Box mb={6}>
                        <Flex justifyContent="space-between" alignItems="center">
                          <Title>CME Events</Title>
                          {R.gt(events.length, 3) && (
                            <Button size="small" as={Link} to="/cme/events">
                              More
                            </Button>
                          )}
                        </Flex>
                      </Box>
                      <Box mx={[0, -4]}>
                        <ListFlex flexDirection={['column', 'row']}>
                          {takeMap(3, item => (
                            <EventCardBox key={item.id} expired={isExpired(item.date)}>
                              <EventCard
                                as={Link}
                                to={`/cme/events/${R.propOr('', 'id', item)}`}
                                {...getEventCardProps({
                                  item,
                                  uri: '/cme/events',
                                })}
                                dateFormat="ccc, dd-MMM-yyyy"
                                height="295px"
                                variant="vertical"
                              />
                            </EventCardBox>
                          ))(events)}
                        </ListFlex>
                      </Box>
                    </Box>
                    {/* -------- CME Videos -------- */}
                    <Flex flexDirection="column">
                      <Box mb={6}>
                        <Flex justifyContent="space-between" alignItems="center">
                          <Title>CME Video</Title>
                          {R.gt(videos.length, 5) && (
                            <Button size="small" as={Link} to="/cme/videos">
                              More
                            </Button>
                          )}
                        </Flex>
                      </Box>
                      <ListFlex flexDirection="column">
                        {takeMap(5, item => (
                          <Box key={item.id} mb={5}>
                            <ArticleCard
                              as={Link}
                              to={`/cme/videos/${R.propOr('', 'id', item)}`}
                              {...getArticleCardProps({
                                item,
                                uri: '/cme/videos',
                              })}
                              height={['389px', '220px']}
                            />
                          </Box>
                        ))(videos)}
                      </ListFlex>
                    </Flex>
                  </Flex>
                </Box>
              </Flex>
            </Section>
          </>
        );
      }}
    </Query>
  );
};

export default Home;
